import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Scale, Calculator, Briefcase, Building2, ShieldCheck, Clock, Users } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1515703944563-dbcfbf121b3d?auto=format&fit=crop&q=85&w=2000";

const services = [
  {
    icon: Scale,
    tag: "01",
    title: "Fiscal",
    desc: "Planificación tributaria, IRPF, IVA, Sociedades y representación ante inspecciones de Hacienda.",
    span: "md:col-span-7",
  },
  {
    icon: Calculator,
    tag: "02",
    title: "Contable",
    desc: "Contabilidad al día, cierres mensuales y cuentas anuales listas para decisiones.",
    span: "md:col-span-5",
  },
  {
    icon: Briefcase,
    tag: "03",
    title: "Laboral",
    desc: "Nóminas, seguros sociales, contratos y todo el ciclo laboral de tu plantilla.",
    span: "md:col-span-5",
  },
  {
    icon: Building2,
    tag: "04",
    title: "Mercantil",
    desc: "Constitución de sociedades, actas, operaciones mercantiles y representación empresarial.",
    span: "md:col-span-7",
  },
];

const stats = [
  { value: "25+", label: "Años de experiencia" },
  { value: "200+", label: "Empresas asesoradas" },
  { value: "4", label: "Áreas de práctica" },
  { value: "100%", label: "Compromiso" },
];

export default function Home() {
  return (
    <div className="text-zinc-200">
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Oficina moderna"
            className="w-full h-full object-cover opacity-40 animate-float-slow"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.32em] text-[#D4AF37] mb-6 border border-[#D4AF37]/30 px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
              Asesoría integral · Cataluña
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif text-5xl md:text-7xl tracking-tight text-white leading-[1.02] mb-8"
            >
              Tu mejor <em className="gold-text not-italic">aliado</em>
              <br />
              para tu día a día
              <br />
              <span className="text-zinc-500">fiscal y contable.</span>
            </h1>
            <p className="text-lg text-zinc-400 leading-relaxed max-w-xl mb-10">
              Más de 25 años ofreciendo soluciones integrales y personalizadas. Desde la gestión diaria
              hasta la representación ante inspecciones administrativas.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/login"
                data-testid="hero-cta-login"
                className="group px-7 py-3.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors flex items-center gap-2"
              >
                Acceder al área cliente
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/servicios"
                data-testid="hero-cta-services"
                className="px-7 py-3.5 rounded-full border border-white/15 text-white hover:border-[#D4AF37]/60 hover:text-[#D4AF37] transition-colors"
              >
                Ver servicios
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/5 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <div className="font-serif text-4xl md:text-5xl gold-text">{s.value}</div>
              <div className="text-xs uppercase tracking-[0.22em] text-zinc-500 mt-2">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] mb-4">
              Nuestros servicios
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-white max-w-2xl leading-tight">
              Soluciones integrales para <em className="gold-text not-italic">cada etapa</em> de tu negocio.
            </h2>
          </div>
          <Link
            to="/servicios"
            className="text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors flex items-center gap-2"
          >
            Explorar todo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-12 gap-4">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`${s.span} col-span-12 group relative bg-[#121214] border border-white/5 rounded-2xl p-8 md:p-10 hover:border-[#D4AF37]/40 transition-colors overflow-hidden`}
                data-testid={`service-card-${s.title.toLowerCase()}`}
              >
                <div className="flex items-start justify-between mb-16">
                  <div className="w-12 h-12 rounded-lg border border-[#D4AF37]/30 grid place-items-center">
                    <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-xs text-zinc-600">{s.tag}</span>
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-white mb-3">{s.title}</h3>
                <p className="text-zinc-400 leading-relaxed max-w-md">{s.desc}</p>
                <div className="mt-6 text-sm text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                  Saber más <ArrowRight className="w-4 h-4" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#08080a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 grid md:grid-cols-3 gap-10">
          {[
            {
              icon: ShieldCheck,
              t: "Portal seguro",
              d: "Acceso privado con tu usuario para consultar y descargar tus documentos las 24 horas.",
            },
            {
              icon: Clock,
              t: "Al día del calendario fiscal",
              d: "Vigilamos plazos y obligaciones para que nunca te enfrentes a una sanción evitable.",
            },
            {
              icon: Users,
              t: "Trato cercano y humano",
              d: "Un equipo que conoce tu negocio y responde con criterio, no con formularios.",
            },
          ].map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.t}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="border-l border-[#D4AF37]/30 pl-6"
              >
                <Icon className="w-6 h-6 text-[#D4AF37] mb-6" strokeWidth={1.5} />
                <h4 className="font-serif text-2xl text-white mb-3">{b.t}</h4>
                <p className="text-zinc-400 leading-relaxed">{b.d}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <div className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] mb-6">
          ¿Empezamos?
        </div>
        <h2 className="font-serif text-4xl md:text-6xl text-white max-w-3xl mx-auto leading-tight mb-10">
          Deja que nos ocupemos de <em className="gold-text not-italic">lo importante</em>.
        </h2>
        <Link
          to="/contacto"
          className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#FDE047] transition-colors"
          data-testid="home-cta-contact"
        >
          Solicitar cita <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
